import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { calculateTriangleArea, scaledCoordinate } from '../drawing.helper'

export type TrianglePatternDrawingState = {
    a?: Point,
    b?: Point,
    c?: Point,
    d?: Point
}


export type TrianglePatternDrawingSettings = {
    lineColor: ColorValue,
    fillColor: ColorValue,
    lineWidth: number
}


class TrianglePatternDrawingTool extends DrawingBase<any, TrianglePatternDrawingSettings, TrianglePatternDrawingState> {
    scaledCoordinates: Record<string, PointCoordinates> = {}
    slopes = {
        top: 0,
        bottom: 0
    }
    intercepts = {
        top: 0,
        bottom: 0
    }
    trianglePoints: Record<string, PointCoordinates> = {
        p1: { x: 0, y: 0 },
        p2: { x: 0, y: 0 },
        p3: { x: 0, y: 0 }
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { a, b, c, d } = this.scaledCoordinates

        let prevPoint: PointCoordinates

        ctx.beginPath()
        this.setLineAttributes(ctx)
        const points = [a, b, c, d]
        points.forEach((p, i) => {
            if (!p) return

            if (!prevPoint) {
                ctx.moveTo(p.x, p.y)
            } else {
                ctx.lineTo(p.x, p.y)
            }
            prevPoint = p
        })

        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.stroke()

        ctx.closePath()

        ctx.beginPath()
        const { p1, p2, p3 } = this.trianglePoints
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.lineTo(p3.x, p3.y)
        ctx.lineTo(p1.x, p1.y)

        ctx.fillStyle = this._settings.fillColor.color
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fill()
        ctx.globalAlpha = 0.5
        const dotWidth = 2 * window.devicePixelRatio
        let lineDash: number[] = [2 * dotWidth, dotWidth]
        ctx.setLineDash(lineDash)
        ctx.stroke()
        ctx.closePath()

        this.drawPointsLabels(ctx)
    };


    drawPointsLabels(ctx: CanvasRenderingContext2D) {
        const { a, b, c, d } = this._state
        const points = [a, b, c, d].filter(v => v)

        points.forEach((p, i) => {
            const refPoint = i == 0 ? i + 1 : i - 1

            const position = points[refPoint] && points[refPoint]!.y > p!.y ? -1 : 1
            const renderCoords = this.scaledCoordinates[p!.id]
            const text = p!.id.toUpperCase()
            ctx.font = (12 * this.verticalPixelRatio) + 'px sans-serif'
            ctx.textBaseline = 'top'
            ctx.globalAlpha = 1
            const padding = 4 * devicePixelRatio
            const dim = ctx.measureText(text)
            const textWidth = dim.width
            const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
            const startX = renderCoords.x - textWidth / 2
            const startY = renderCoords.y - (position > 0 ? textHeight + 2 * padding : -2 * padding)
            ctx.fillStyle = this._settings.lineColor.color
            ctx.beginPath()
            ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * this.verticalPixelRatio)
            ctx.fill()
            ctx.closePath()
            ctx.fillStyle = '#fff'
            ctx.fillText(text, startX, startY)
        })
    }

    drawRatioText(ctx: CanvasRenderingContext2D, text: string, start: PointCoordinates, end: PointCoordinates) {
        ctx.font = (12 * this.verticalPixelRatio) + 'px sans-serif'
        ctx.textBaseline = 'top'
        ctx.globalAlpha = 1
        const dim = ctx.measureText(text)
        const padding = 4 * window.devicePixelRatio
        const textWidth = dim.width
        const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
        const centerX = (start.x + end.x) / 2
        const centerY = (start.y + end.y) / 2
        const startX = centerX - textWidth / 2
        const startY = centerY - textHeight
        ctx.fillStyle = this._settings.lineColor.color
        ctx.beginPath()
        ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * this.verticalPixelRatio)
        ctx.fill()
        ctx.closePath()
        ctx.fillStyle = '#fff'
        ctx.fillText(text, startX, startY)
    }

    calculateLineAttributes() {
        const { a, b, c, d } = this._state
        const points = [a, b, c, d]

        points.forEach((p) => {
            if (!p) return
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)
            if (x && y) {
                this.scaledCoordinates[p.id] = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }
            }
        })
        const { a: as, b: bs, c: cs, d: ds } = this.scaledCoordinates

        if (as && bs && cs && ds) {
            this.slopes.top = (bs.y - ds.y) / (bs.x - ds.x)
            this.intercepts.top = bs.y - this.slopes.top * bs.x
            this.slopes.bottom = (as.y - cs.y) / (as.x - cs.x)
            this.intercepts.bottom = as.y - this.slopes.bottom * as.x
            const p1x = (this.intercepts.top - this.intercepts.bottom) / (this.slopes.bottom - this.slopes.top)
            const p1y = this.slopes.top * p1x + this.intercepts.top
            let p2x = 0, p2y = 0, p3x = 0, p3y = 0
            const minX = Math.min(as.x, bs.x, cs.x, ds.x)
            const maxX = Math.max(as.x, bs.x, cs.x, ds.x)
            let x = minX
            if (p1x < minX) {
                x = maxX
            }
            p2x = x
            p3x = x

            p2y = this.slopes.top * p2x + this.intercepts.top
            p3y = this.slopes.bottom * p3x + this.intercepts.bottom

            this.trianglePoints.p1 = {
                x: p1x,
                y: p1y
            }
            this.trianglePoints.p2 = {
                x: p2x,
                y: p2y
            }
            this.trianglePoints.p3 = {
                x: p3x,
                y: p3y
            }
        }


    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = false) {
        if (setLineType) {
            const dotWidth = Math.max(2, this._settings.lineWidth) * this.horizontalPixelRatio
            let lineDash: number[] = [dotWidth, dotWidth]
            ctx.setLineDash(lineDash)
        }
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'a',
            x: time,
            y: price
        }

        if (!this.lastAddedPoint) {
            this._state.a = point
        } else if (this.lastAddedPoint.id == 'a') {
            point.id = 'b'
            this._state.b = point
        } else if (this.lastAddedPoint.id == 'b') {
            point.id = 'c'
            this._state.c = point
        } else {
            point.id = 'd'
            this._state.d = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const { a, b, c, d } = this._state

        return [a!, b!, c!, d!].filter(p => p)
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1, p2, p3 } = this.trianglePoints
        if (this.isWithinTriangle(scaledX, scaledY, p1, p2, p3)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    isWithinTriangle(x: number, y: number, p1: PointCoordinates, p2: PointCoordinates, p3: PointCoordinates) {
        const area = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
        const area1 = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, x, y)
        const area2 = calculateTriangleArea(p2.x, p2.y, p3.x, p3.y, x, y)
        const area3 = calculateTriangleArea(p1.x, p1.y, p3.x, p3.y, x, y)

        return area !== 0 && Number(area.toFixed(1)) === Number((area1 + area2 + area3).toFixed(1))
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'a':
                    this._state.a = h
                    break
                case 'b':
                    this._state.b = h
                    break
                case 'c':
                    this._state.c = h
                    break
                case 'd':
                    this._state.d = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        if (!this._state[id as keyof typeof this._state]) return
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { a, b, c, d } = this._state
        const points = [a, b, c, d].filter(v => v)
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a!.y - b!.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0]!.y,
                p2: sorted[sorted.length - 1]!.y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c]!.id,
                p1: sorted[c]!.y,
                p2: sorted[c]!.y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { a, b, c, d } = this._state
        const points = [a, b, c, d].filter(v => v)
        if (points.length < 2) return []
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a!.x as number) - (b!.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0]!.x,
                p2: sorted[sorted.length - 1]!.x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c]!.id,
                p1: sorted[c]!.x,
                p2: sorted[c]!.x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<TrianglePatternDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#673ab7',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#673ab7',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },

                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): TrianglePatternDrawingSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        return 'trianglePattern'
    }

    getMiniSettings = () => {
        return ['fillColor', 'lineColor', 'lineWidth'] as (keyof TrianglePatternDrawingSettings)[]
    }
}

export default TrianglePatternDrawingTool